import { imgBreakpoints } from '@/theme/breakpoints';
import parseAsNumber from './parseAsNumber';
// Must be the same as "\src\Maybourne.Optimizely.Web\appsettings.json ImageResize ImageAllowedWidths"
export const IMAGE_WIDTHS = [
    320, 412, 480, 560, 575, 640, 720, 800, 880, 960, 1040, 1120, 1200, 1280, 1360, 1440, 1520, 1600, 1680, 1760, 1840,
    1920, 2160, 2320,
];
export const DEFAULT_QUALITY = 70;
export function createImageUrl({ baseUrl, format, focalPoint, quality = DEFAULT_QUALITY, ratio, useCdn = false, width, }) {
    if (useCdn) {
        return createCDNImageUrl({ width, quality, ratio, baseUrl, format, focalPoint });
    }
    else {
        return createDefaultImageUrl({ width, quality, ratio, baseUrl, format, focalPoint });
    }
}
export function createDefaultImageUrl({ width: rawWidth, quality = DEFAULT_QUALITY, ratio: rawRatio, baseUrl, format, focalPoint, }) {
    const ratio = parseAsNumber(rawRatio);
    const width = parseAsNumber(rawWidth);
    const height = Math.floor(width / ratio);
    const searchParams = new URLSearchParams();
    if (focalPoint) {
        searchParams.append('rxy', focalPoint);
    }
    if (format) {
        searchParams.append('format', format);
    }
    if (height) {
        searchParams.append('height', height.toString());
    }
    if (quality) {
        searchParams.append('quality', quality.toString());
    }
    if (width) {
        searchParams.append('width', width.toString());
    }
    return `${baseUrl}?${searchParams}`;
}
/**
 * Generate a URL which is compatible with Cloudflare's CDN.
 * @see {@link https://docs.developers.optimizely.com/digital-experience-platform/docs/image-resizing-using-cdn#use-cloudflares-image-resizing-feature-and-the-url-format}
 * @param imageParams The image parameters
 * @returns The CDN compatible URL
 */
export function createCDNImageUrl({ baseUrl, focalPoint, format, quality = DEFAULT_QUALITY, ratio: rawRatio, width: rawWidth, }) {
    const ratio = parseAsNumber(rawRatio);
    const width = parseAsNumber(rawWidth);
    const height = Math.floor(width / ratio);
    const urlParams = [
        // include default parameters here first
        'fit=cover',
    ];
    if (focalPoint) {
        // Old format was 0.5,0.5 but Cloudflare expects 0.5x0.5
        urlParams.push(`gravity=${focalPoint.replace(',', 'x')}`);
    }
    if (format) {
        urlParams.push(`format=${format}`);
    }
    if (height) {
        urlParams.push(`height=${height}`);
    }
    if (quality) {
        urlParams.push(`quality=${quality}`);
    }
    if (width) {
        urlParams.push(`width=${width}`);
    }
    // /cdn-cgi/image/<OPTIONS>/<SOURCE-IMAGE>
    return `/cdn-cgi/image/${urlParams.join(',')}${baseUrl}`;
}
export function generateSrcSet({ ...params }) {
    const getWidth = (desiredWidth) => {
        const fallback = IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
        return IMAGE_WIDTHS.find((width) => width >= desiredWidth) ?? fallback;
    };
    const breakpointWidths = Object.keys(imgBreakpoints).map((value) => parseAsNumber(value));
    return (breakpointWidths
        .reduce((acc, breakpointWidth) => {
        const key = imgBreakpoints[breakpointWidth].breakpoint;
        const desiredWidth = imgBreakpoints[breakpointWidth].desiredWidth;
        const breakpoint = params.sizes?.[key];
        const fallbackRatio = acc.ratio;
        const fallbackVw = acc.vw || 1;
        const ratio = breakpoint?.ratio ? Number(breakpoint?.ratio) || 1 : fallbackRatio;
        const vw = breakpoint?.vw ? Number(breakpoint?.vw || 100) : fallbackVw;
        const create = (format) => {
            const src = createImageUrl({ ...params, ratio, format, width: getWidth(desiredWidth * vw) });
            const srcHdpi = createImageUrl({ ...params, ratio, format, width: getWidth(desiredWidth * vw * 2) });
            return {
                id: `${breakpointWidth}-${format ?? 'default'}`,
                media: breakpointWidth === 0 ? undefined : `(min-width: ${breakpointWidth}px)`,
                type: format ? `image/${format}` : undefined,
                src,
                srcHdpi,
            };
        };
        return {
            ratio,
            vw,
            value: [...acc.value, create(), create('webp')],
        };
    }, { value: [] })
        // Remove duplicate URLs eg if 1280px uses the same image as 1024px, we don't need to include the 1280px breakpoint
        .value.filter((item, pos, items) => items[pos - 2]?.src !== item.src)
        // Reverse because media queries operate on a first match basis, so we want the widest ones first
        .reverse());
}
